import React, { useCallback, useState } from 'react';
import { analyticsUtils } from 'utils/analytics-utils';
import { createElementKey } from 'utils/create-element-key';
import { UtpCard, UtpCardContent, UtpCardHeader, UtpCardMedia, UtpCardText } from '../card';
import { UtpCardGrid } from '../grid';
import { Modal } from '../modal';
import { Section } from '../section';
import { SectionSubTitle } from '../section-subtitle';
import { UtpCardStructure } from './utp-card-section-types';

type UtpCardSectionProps<T> = {
  className?: string;
  elementBlock: string;
  elementText: string;
  renderModalContent: (utpCard: UtpCardStructure<T>, onCloseModal: () => void) => React.ReactNode;
  utpCardList: UtpCardStructure<T>[];
  titleClassName?: string;
  cardClassName?: string;
  title?: string;
};

export const UtpCardSection = <T,>({
  className,
  elementBlock,
  elementText,
  renderModalContent,
  utpCardList,
  titleClassName,
  cardClassName,
  title,
}: UtpCardSectionProps<T>) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeUtpCard, setActiveUtpCard] = useState<UtpCardStructure<T> | null>(null);

  const handleCloseModalFromModalContent = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveUtpCard(null);
    if (activeUtpCard) {
      analyticsUtils.clickButtonEvent(elementBlock, 'Закрыть', activeUtpCard.elementName);
    }
  }, [activeUtpCard, elementBlock]);

  const handleUtpCardClick = useCallback(
    (utpCard: UtpCardStructure<T>) => () => {
      setIsModalOpen(true);
      setActiveUtpCard(utpCard);
      analyticsUtils.clickButtonEvent(
        elementBlock,
        utpCard.elementName,
        elementText
      );
      analyticsUtils.showEvent(elementBlock, 'Модальное окно', `${utpCard.elementName} / modal`);
    },
    [elementBlock, elementText, setIsModalOpen]
  );

  return (
    <Section>
      {title && (
        <SectionSubTitle className={titleClassName} margin="m">
          {title}
        </SectionSubTitle>
      )}
      <UtpCardGrid className={className} cardNumber={utpCardList.length}>
        {utpCardList.map((utpCard: UtpCardStructure<T>, index: number) => (
          <UtpCard
            cardClassName={cardClassName}
            key={createElementKey('utp-card', index)}
            onClick={handleUtpCardClick(utpCard)}
            type={utpCard.type || 'secondary'}>
            <UtpCardContent>
              <UtpCardHeader {...utpCard.header} />
              {utpCard.description && <UtpCardText {...utpCard.description} />}
            </UtpCardContent>
            <UtpCardMedia loading="lazy" {...utpCard.image} title={utpCard.image.alt} />
          </UtpCard>
        ))}
      </UtpCardGrid>

      {activeUtpCard && (
        <Modal
          onClose={handleCloseModal}
          show={isModalOpen}
          size={activeUtpCard.modal?.size || 's'}
          variant={activeUtpCard.modal?.variant || 'curtain'}>
          {renderModalContent(activeUtpCard, handleCloseModalFromModalContent)}
        </Modal>
      )}
    </Section>
  );
};

UtpCardSection.defaultProps = {
  titleClassName: '',
  cardClassName: '',
  title: '',
  isNewAnalytics: false,
};
